import { useState, useCallback, useEffect, useMemo } from "react"; // Added useMemo
import {
  Search,
  Plus,
  AlertCircle // Added AlertCircle for error content
} from "lucide-react";
import { Integration } from "../../types";
import { useIntegrations } from "../../hooks/useIntegrations";
import NewIntegrationDialog from "../dialogs/NewIntegrationDialog";
import TableComponent from "../shared/TableComponent"; // Import TableComponent
import { ColumnDef } from "@tanstack/react-table"; // Import ColumnDef

interface IntegrationsTabProps {
  searchTerm: string;
  setSearchTerm: (term: string) => void;
}

function IntegrationsTab({ searchTerm, setSearchTerm }: IntegrationsTabProps) {
  const [/* showIntegrationMenu */, /* setShowIntegrationMenu */] = useState<string | null>(null);
  const [typeFilter, setTypeFilter] = useState<string>('All');
  const [showNewIntegrationDialog, setShowNewIntegrationDialog] = useState(false);
  const [editingIntegration, setEditingIntegration] = useState<Integration | null>(null);

  const {
    integrations,
    isLoading: isLoadingIntegrations,
    loadIntegrations,
    error: integrationsError, // Capture error from useIntegrations
  } = useIntegrations();

  const refreshIntegrations = useCallback((search?: string) => {
    loadIntegrations({ search }); // Error handling is now in TableComponent
  }, [loadIntegrations]);

  // Load integrations when search term changes
  useEffect(() => {
    refreshIntegrations(searchTerm);
  }, [searchTerm, refreshIntegrations]);

  const filteredIntegrations = integrations.filter(integration => {
    if (typeFilter === 'All') return true;
    switch (typeFilter) {
      case 'Destination':
        return integration.destination;
      case 'Sync Injector':
        return integration.sync_injector;
      case 'Both':
        return integration.destination && integration.sync_injector;
    }
  });

  const handleTypeFilterChange = (event: React.ChangeEvent<HTMLSelectElement>) => {
    setTypeFilter(event.target.value);
  };


  const handleCloseDialog = () => {
    setShowNewIntegrationDialog(false);
    setEditingIntegration(null);
  };

  const integrationColumns: ColumnDef<Integration>[] = useMemo(() => [
    {
      accessorKey: 'name',
      header: 'Integration',
      cell: ({ row }) => (
        <div
          className="flex items-center cursor-pointer hover:bg-gray-50 dark:hover:bg-gray-700 rounded-lg p-2"
          onClick={() => {
            setEditingIntegration(row.original);
            setShowNewIntegrationDialog(true);
          }}
        >
          <div className="h-10 w-10 flex-shrink-0">
            <img
              className="h-10 w-10 rounded-lg"
              src={row.original.logo_url}
              alt={row.original.name}
            />
          </div>
          <div className="ml-4">
            <div className="text-sm font-medium text-gray-900 dark:text-gray-100">
              {row.original.name}
            </div>
          </div>
        </div>
      ),
    },
    {
      accessorKey: 'category',
      header: 'Category',
      cell: ({ getValue }) => (
        <div className="text-sm text-gray-900 dark:text-gray-100">
          {getValue() as string}
        </div>
      ),
    },
    {
      accessorKey: 'enabled',
      header: 'Status',
      cell: ({ getValue }) => {
        const enabled = getValue() as boolean;
        return (
          <span
            className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${
              enabled
                ? "bg-green-500/10 text-green-800 dark:text-green-200"
                : "bg-yellow-500/10 text-yellow-800 dark:text-yellow-200"
            }`}
          >
            {enabled ? "Enabled" : "Disabled"}
          </span>
        );
      },
    },
    {
      id: 'type', // Custom ID since it's derived
      header: 'Type',
      cell: ({ row }) => {
        const { destination, sync_injector } = row.original;
        let typeText = 'Unknown';
        let typeClass = 'bg-gray-500/10 text-gray-800 dark:text-gray-200'; // Default gray

        if (destination && sync_injector) {
          typeText = 'Both';
          typeClass = 'bg-blue-500/10 text-blue-800 dark:text-blue-200';
        } else if (destination) {
          typeText = 'Destination';
          typeClass = 'bg-purple-500/10 text-purple-800 dark:text-purple-200'; // Example color
        } else if (sync_injector) {
          typeText = 'Sync Injector';
          typeClass = 'bg-orange-500/10 text-orange-800 dark:text-orange-200'; // Example color
        }

        return (
          <span className={`inline-flex items-center px-2.5 py-0.5 rounded-full text-xs font-medium ${typeClass}`}>
            {typeText}
          </span>
        );
      },
    },
    {
      id: 'actions',
      header: '',
      cell: () => (
        <div className="relative flex items-center justify-end gap-4">
          {/* Actions were empty in original, keeping it that way */}
        </div>
      ),
    },
  ], [setEditingIntegration, setShowNewIntegrationDialog]);

  return (
    <div className="integrations-tab">
      <div className="mb-6 flex justify-between items-center">
        <div className="relative flex-1 flex gap-4">
          <input
            type="text"
            placeholder="Search integrations"
            value={searchTerm}
            onChange={(e) => setSearchTerm(e.target.value)}
            className="w-full pl-10 pr-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-200 dark:placeholder-gray-400"
          />
          <Search className="absolute left-3 top-2.5 h-5 w-5 text-gray-400 dark:text-gray-500" />
          <select
            value={typeFilter}
            onChange={handleTypeFilterChange}
            className="px-4 py-2 border border-gray-300 dark:border-gray-600 rounded-lg focus:ring-blue-500 focus:border-blue-500 dark:bg-gray-800 dark:text-gray-200"
          >
            <option value="All">All Types</option>
            <option value="Destination">Data Destination</option>
            <option value="Both">Both</option>
            <option value="Sync Injector">Sync Injector</option>
          </select>
        </div>
        <button
          onClick={() => setShowNewIntegrationDialog(true)}
          className="ml-4 px-4 py-2 bg-blue-600 text-white rounded-lg hover:bg-blue-700 dark:bg-blue-700 dark:hover:bg-blue-800 focus:outline-none focus:ring-2 focus:ring-blue-500 focus:ring-offset-2 dark:focus:ring-offset-gray-900 flex items-center gap-2"
        >
          <Plus className="h-5 w-5" />
          New Integration
        </button>
      </div>

      <TableComponent
        title="Integrations"
        data={filteredIntegrations}
        columns={integrationColumns}
        isLoading={isLoadingIntegrations}
        isError={!!integrationsError}
        errorContent={
          <div className="flex items-center justify-center py-4 text-red-500">
            <AlertCircle className="h-8 w-8 mr-3" />
            <span>Failed to load integrations.</span>
          </div>
        }
        buttons={null} // Buttons are handled by the header
      />
      {showNewIntegrationDialog && (
        <NewIntegrationDialog
          onClose={handleCloseDialog}
          isEdit={!!editingIntegration}
          initialData={editingIntegration}
          onIntegrationCreated={refreshIntegrations}
        />
      )}
    </div>
  );
}

export default IntegrationsTab;
